import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useRecurringExpenses,
  useDeleteRecurringExpense,
  useUpdateRecurringExpense,
  useRecurringExpenseEditHistory,
} from '@/hooks/useRecurringExpenses';
import { useExpenses, useCreateExpense } from '@/hooks/useExpenses';
import { useCurrentAssistant } from '@/hooks/useAssistantAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Pencil, Trash2, DollarSign, Plus, Calendar, History } from 'lucide-react';
import { format, startOfMonth, eachMonthOfInterval } from 'date-fns';
import { fr, arSA } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import type { RecurringExpense, AssistantPermissions } from '@/types/entities';
import type { RecurringExpenseWithAudit } from '@/hooks/useRecurringExpenses';
import { ConfirmModal } from '@/components/ui/confirm-modal';
import { AlertModal } from '@/components/ui/alert-modal';
import { AddRecurringExpenseDialog } from './AddRecurringExpenseDialog';
import { EditRecurringExpenseDialog } from './EditRecurringExpenseDialog';
import { AccountingEditHistoryPanel } from './AccountingEditHistoryPanel';

interface RecurringExpensesListProps {
  restaurantId: string;
  startDate?: string;
  endDate?: string;
}

interface RecurringPayment {
  recurringExpenseId: string;
  nameAr: string;
  nameFr: string;
  categoryNameAr: string;
  categoryNameFr: string;
  categoryColor: string;
  monthlyAmount: number;
  paidAmount: number;
  paymentCount: number;
  lastPaymentDate: string | null;
  isActive: boolean;
  paidMonths: Set<string>; // Set of month keys (YYYY-MM format)
  unpaidMonths: Date[]; // Array of unpaid month dates
}

export function RecurringExpensesList({
  restaurantId,
  startDate,
  endDate,
}: RecurringExpensesListProps) {
  const { t, i18n } = useTranslation();
  const { data: assistant } = useCurrentAssistant();
  const {
    data: recurringExpenses,
    isLoading: recurringLoading,
    refetch: refetchRecurring,
  } = useRecurringExpenses(restaurantId, true); // Include deleted items
  const deleteRecurringMutation = useDeleteRecurringExpense(restaurantId);
  const updateRecurringMutation = useUpdateRecurringExpense(restaurantId);
  const createExpenseMutation = useCreateExpense(restaurantId);

  // Permission helper for assistants
  const hasPermission = (page: keyof AssistantPermissions, action: string): boolean => {
    if (!assistant?.permissions || !assistant.permissions[page]) return false;
    const pagePermissions = assistant.permissions[page] as Record<string, boolean>;
    return pagePermissions[action] === true;
  };

  // Check if user is an assistant
  const isAssistant = !!assistant;

  // Permission checks (admins always have permission)
  const canAdd = !isAssistant || hasPermission('expenses', 'add');
  const canEdit = !isAssistant || hasPermission('expenses', 'edit');
  const canDelete = !isAssistant || hasPermission('expenses', 'delete');

  // Query expenses with recurring_expense_id - need ALL payments to check paid months
  // but also filtered ones for display
  const allExpenseFilters = {
    limit: 1000, // Get all recurring expense payments
  };
  const filteredExpenseFilters: {
    startDate?: string;
    endDate?: string;
    limit: number;
  } = {
    limit: 1000, // Get recurring expense payments for the period
  };
  if (startDate) filteredExpenseFilters.startDate = startDate;
  if (endDate) filteredExpenseFilters.endDate = endDate;

  // Get all expenses to check which months are paid
  const {
    data: allExpensesData,
    isLoading: allExpensesLoading,
    refetch: refetchAllExpenses,
  } = useExpenses(restaurantId, allExpenseFilters);

  // Get filtered expenses query (for refetch and loading state)
  const {
    isLoading: expensesLoading,
    refetch: refetchExpenses,
  } = useExpenses(restaurantId, filteredExpenseFilters);

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingRecurring, setEditingRecurring] = useState<RecurringExpense | null>(null);
  const [payingRecurringId, setPayingRecurringId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
  });
  const [alertDialog, setAlertDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
  }>({
    open: false,
    title: '',
    description: '',
  });
  const [historyRecurringId, setHistoryRecurringId] = useState<string | null>(null);

  // Get edit history for selected recurring expense
  const { data: editHistory, isLoading: historyLoading } = useRecurringExpenseEditHistory(
    restaurantId,
    historyRecurringId
  );

  const locale = i18n.language === 'ar' ? arSA : fr;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(i18n.language === 'ar' ? 'ar-MA' : 'fr-MA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Process recurring expense payments
  const recurringPayments: RecurringPayment[] = [];
  if (recurringExpenses && allExpensesData?.expenses) {
    // Filter expenses that are recurring expense payments (ALL payments)
    const allRecurringExpensesPayments = allExpensesData.expenses.filter(
      (e) => e.recurringExpenseId
    );

    // Group by recurring expense
    recurringExpenses.forEach((recurring) => {
      // Use ALL payments to check which months are paid
      const allRecurringPaymentsList = allRecurringExpensesPayments.filter(
        (e) => e.recurringExpenseId === recurring.id
      );

      // Use ALL payments for display amounts (not filtered by date range)
      const paidAmount = allRecurringPaymentsList.reduce((sum, e) => sum + e.amount, 0);
      const lastPayment = allRecurringPaymentsList.sort(
        (a, b) => new Date(b.expenseDate).getTime() - new Date(a.expenseDate).getTime()
      )[0];

      // Track paid months (YYYY-MM format) - use ALL payments
      const paidMonths = new Set<string>();
      allRecurringPaymentsList.forEach((expense) => {
        const expenseDate = new Date(expense.expenseDate);
        const monthKey = format(expenseDate, 'yyyy-MM');
        paidMonths.add(monthKey);
      });

      // Find unpaid months from start date to current month (regardless of date filter)
      const startDate = new Date(recurring.startDate);
      const startMonth = startOfMonth(startDate);
      const now = startOfMonth(new Date()); // Current month

      // Generate all months from start date to current month
      const allMonthsSinceStart = startMonth <= now
        ? eachMonthOfInterval({ start: startMonth, end: now })
        : [];

      // Filter out paid months
      const unpaidMonths = allMonthsSinceStart.filter((month) => {
        const monthKey = format(month, 'yyyy-MM');
        return !paidMonths.has(monthKey);
      });

      recurringPayments.push({
        recurringExpenseId: recurring.id,
        nameAr: recurring.nameAr,
        nameFr: recurring.nameFr,
        categoryNameAr: recurring.categoryNameAr || '',
        categoryNameFr: recurring.categoryNameFr || '',
        categoryColor: recurring.categoryColor || '#6b7280',
        monthlyAmount: recurring.amount,
        paidAmount,
        paymentCount: allRecurringPaymentsList.length,
        lastPaymentDate: lastPayment?.expenseDate || null,
        isActive: recurring.isActive,
        paidMonths,
        unpaidMonths,
      });
    });
  }

  // Calculate totals
  const totalPaid = recurringPayments.reduce((sum, p) => sum + p.paidAmount, 0);
  const totalExpected = recurringPayments
    .filter((p) => p.isActive)
    .reduce((sum, p) => sum + p.monthlyAmount, 0);

  const handleDelete = (recurring: RecurringExpense) => {
    setConfirmDialog({
      open: true,
      title: t('accounting.deleteRecurringExpense'),
      description: t('accounting.deleteRecurringExpenseConfirm'),
      onConfirm: async () => {
        setDeletingId(recurring.id);
        try {
          await deleteRecurringMutation.mutateAsync(recurring.id);
          await refetchRecurring();
        } catch (error) {
          console.error('Failed to delete recurring expense:', error);
          setAlertDialog({
            open: true,
            title: t('error.error'),
            description: t('error.generic'),
          });
        } finally {
          setDeletingId(null);
        }
      },
    });
  };

  const handleToggleActive = async (recurring: RecurringExpense) => {
    try {
      const payload: any = {
        recurringExpenseId: recurring.id,
        categoryId: recurring.categoryId,
        nameAr: recurring.nameAr,
        nameFr: recurring.nameFr,
        amount: recurring.amount,
        isActive: !recurring.isActive,
      };
      if (recurring.notes) payload.notes = recurring.notes;

      await updateRecurringMutation.mutateAsync(payload);
      await refetchRecurring();
    } catch (error) {
      console.error('Failed to toggle recurring expense:', error);
      setAlertDialog({
        open: true,
        title: t('error.error'),
        description: t('error.generic'),
      });
    }
  };

  const handlePayRecurringForMonth = async (recurring: RecurringExpense, monthDate: Date) => {
    setPayingRecurringId(recurring.id);
    try {
      // Use the first day of the month as the payment date
      const paymentDate = format(startOfMonth(monthDate), 'yyyy-MM-dd');
      await createExpenseMutation.mutateAsync({
        categoryId: recurring.categoryId,
        expenseDate: paymentDate,
        amount: recurring.amount,
        descriptionAr: recurring.nameAr,
        descriptionFr: recurring.nameFr,
        recurringExpenseId: recurring.id,
      });
      // Refetch both filtered and all expenses to update paid months tracking
      await Promise.all([refetchExpenses(), refetchAllExpenses()]);
      setAlertDialog({
        open: true,
        title: t('common.success'),
        description: t('accounting.paymentRecorded'),
      });
    } catch (error) {
      console.error('Failed to record payment:', error);
      setAlertDialog({
        open: true,
        title: t('error.error'),
        description: error instanceof Error ? error.message : t('error.generic'),
      });
    } finally {
      setPayingRecurringId(null);
    }
  };

  const isLoading = recurringLoading || expensesLoading || allExpensesLoading;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <div className="h-10 w-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {canAdd && (
        <div className="flex justify-end">
          <Button onClick={() => setShowAddDialog(true)}>
            <Plus className="w-4 h-4 me-2" />
            {t('accounting.addRecurringExpense')}
          </Button>
        </div>
      )}

      {/* Recurring Payments Table */}
      {startDate && endDate && (
        <Card>
          <CardHeader>
            <CardTitle>{t('accounting.recurringPayments')}</CardTitle>
          </CardHeader>
          <CardContent>
            {!recurringExpenses || recurringExpenses.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                {t('accounting.noRecurringExpenses')}
              </p>
            ) : (
              <div className="space-y-4">
                {/* Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted rounded-lg">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('accounting.totalExpected')}</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(totalExpected)} {t('common.currency')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('accounting.totalPaid')}</p>
                    <p className="text-xl font-bold text-purple-600">
                      {formatCurrency(totalPaid)} {t('common.currency')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('accounting.paymentCount')}</p>
                    <p className="text-xl font-bold">
                      {recurringPayments.reduce((sum, p) => sum + p.paymentCount, 0)}{' '}
                      {t('accounting.payments')}
                    </p>
                  </div>
                </div>

                {/* Table */}
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('accounting.recurringExpense')}</TableHead>
                        <TableHead>{t('accounting.monthlyAmount')}</TableHead>
                        <TableHead>{t('accounting.paidAmount')}</TableHead>
                        <TableHead>{t('accounting.payments')}</TableHead>
                        <TableHead>{t('accounting.lastPayment')}</TableHead>
                        <TableHead>{t('accounting.createdBy')}</TableHead>
                        <TableHead className="text-right">{t('common.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recurringPayments.map((payment) => {
                        const recurring = recurringExpenses?.find(
                          (r) => r.id === payment.recurringExpenseId
                        ) as RecurringExpenseWithAudit | undefined;
                        if (!recurring) return null;

                        const isDeleted = !!recurring.deletedAt;
                        const isPaying = payingRecurringId === recurring.id;
                        const hasUnpaidMonths = payment.unpaidMonths.length > 0;
                        const currentMonth = new Date();
                        const currentMonthKey = format(currentMonth, 'yyyy-MM');

                        return (
                          <TableRow
                            key={payment.recurringExpenseId}
                            className={cn(
                              !payment.isActive && 'opacity-60',
                              isDeleted && 'opacity-60 bg-muted/30'
                            )}
                          >
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {isDeleted && (
                                  <Badge variant="destructive" className="text-xs">
                                    {t('accounting.deleted')}
                                  </Badge>
                                )}
                                <Badge
                                  variant="outline"
                                  className={cn(isDeleted && 'line-through')}
                                  style={{
                                    borderColor: payment.categoryColor,
                                    color: payment.categoryColor,
                                  }}
                                >
                                  {i18n.language === 'ar'
                                    ? payment.categoryNameAr
                                    : payment.categoryNameFr}
                                </Badge>
                                <span className={cn('font-medium', isDeleted && 'line-through')}>
                                  {i18n.language === 'ar' ? payment.nameAr : payment.nameFr}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="font-mono">
                                {formatCurrency(payment.monthlyAmount)} {t('common.currency')}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {payment.paidAmount > 0 ? (
                                <span className="font-semibold text-purple-600">
                                  {formatCurrency(payment.paidAmount)} {t('common.currency')}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">
                                  {t('accounting.notPaid')}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{payment.paymentCount}</Badge>
                            </TableCell>
                            <TableCell>
                              {payment.lastPaymentDate ? (
                                <span className="text-sm">
                                  {format(new Date(payment.lastPaymentDate), 'dd/MM/yyyy', {
                                    locale,
                                  })}
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-sm">
                                  {t('accounting.noPayment')}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                {recurring.createdByDisplay && (
                                  <span className="text-sm text-muted-foreground block">
                                    {recurring.createdByDisplay}
                                  </span>
                                )}
                                {isDeleted && recurring.updatedByDisplay && (
                                  <span className="text-xs text-muted-foreground italic block">
                                    {t('accounting.deletedBy')}: {recurring.updatedByDisplay}
                                  </span>
                                )}
                                {!recurring.createdByDisplay && !isDeleted && (
                                  <span className="text-muted-foreground text-sm">-</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                {!isDeleted && canEdit && (
                                  <Switch
                                    checked={payment.isActive}
                                    onCheckedChange={() => handleToggleActive(recurring)}
                                  />
                                )}
                                {!isDeleted && canAdd && payment.isActive && hasUnpaidMonths && (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="default" size="sm" disabled={isPaying}>
                                        <DollarSign className="w-4 h-4 me-1" />
                                        {t('accounting.recordPayment')}
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {payment.unpaidMonths.map((month) => {
                                        const monthKey = format(month, 'yyyy-MM');
                                        const isThisMonth = monthKey === currentMonthKey;
                                        return (
                                          <DropdownMenuItem
                                            key={monthKey}
                                            onClick={() =>
                                              handlePayRecurringForMonth(recurring, month)
                                            }
                                            disabled={isPaying}
                                          >
                                            <Calendar className="w-4 h-4 me-2" />
                                            {format(month, 'MMMM yyyy', { locale })}
                                            {isThisMonth && (
                                              <Badge variant="secondary" className="ms-2 text-xs">
                                                {t('accounting.currentMonth')}
                                              </Badge>
                                            )}
                                          </DropdownMenuItem>
                                        );
                                      })}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                                {!isDeleted && payment.isActive && !hasUnpaidMonths && (
                                  <Badge variant="outline" className="text-green-600">
                                    {t('accounting.allPaid')}
                                  </Badge>
                                )}
                                {!isDeleted && canEdit && (
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => setEditingRecurring(recurring)}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                )}
                                {!isDeleted && canDelete && (
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => handleDelete(recurring)}
                                    disabled={deletingId === recurring.id}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setHistoryRecurringId(recurring.id)}
                                  title={t('accounting.editHistory.title')}
                                >
                                  <History className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recurring Expenses Cards (fallback if no date range) */}
      {(!startDate || !endDate) && (
        <>
          {!recurringExpenses || recurringExpenses.length === 0 ? (
            <Card>
              <CardContent className="py-8">
                <p className="text-center text-muted-foreground">
                  {t('accounting.noRecurringExpenses')}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {recurringExpenses.map((recurring) => {
                const recurringWithAudit = recurring as RecurringExpenseWithAudit;
                const isDeleted = !!recurringWithAudit.deletedAt;

                return (
                  <Card
                    key={recurring.id}
                    className={cn(
                      !recurring.isActive && 'opacity-60',
                      isDeleted && 'opacity-60 bg-muted/30'
                    )}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {isDeleted && (
                              <Badge variant="destructive" className="text-xs">
                                {t('accounting.deleted')}
                              </Badge>
                            )}
                            <Badge
                              variant="outline"
                              className={cn(isDeleted && 'line-through')}
                              style={{
                                borderColor: recurring.categoryColor,
                                color: recurring.categoryColor,
                              }}
                            >
                              {i18n.language === 'ar'
                                ? recurring.categoryNameAr
                                : recurring.categoryNameFr}
                            </Badge>
                          </div>
                          <h3 className={cn('font-semibold truncate', isDeleted && 'line-through')}>
                            {i18n.language === 'ar' ? recurring.nameAr : recurring.nameFr}
                          </h3>
                        </div>
                        {!isDeleted && canEdit && (
                          <Switch
                            checked={recurring.isActive}
                            onCheckedChange={() => handleToggleActive(recurring)}
                          />
                        )}
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            {t('accounting.monthlyAmount')}
                          </span>
                          <Badge
                            variant="secondary"
                            className={cn('font-mono', isDeleted && 'line-through')}
                          >
                            {formatCurrency(recurring.amount)} {t('common.currency')}
                          </Badge>
                        </div>
                        {recurring.notes && (
                          <p className={cn('text-xs text-muted-foreground truncate', isDeleted && 'line-through')}>
                            {recurring.notes}
                          </p>
                        )}
                        {recurringWithAudit.createdByDisplay && (
                          <p className="text-xs text-muted-foreground italic">
                            {t('accounting.createdBy')}: {recurringWithAudit.createdByDisplay}
                          </p>
                        )}
                        {isDeleted && recurringWithAudit.updatedByDisplay && (
                          <p className="text-xs text-muted-foreground italic">
                            {t('accounting.deletedBy')}: {recurringWithAudit.updatedByDisplay}
                          </p>
                        )}
                      </div>

                      <div className="flex gap-2 pt-3 border-t">
                        {!isDeleted && canEdit && (
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setEditingRecurring(recurring)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}
                        {!isDeleted && canDelete && (
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleDelete(recurring)}
                            disabled={deletingId === recurring.id}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setHistoryRecurringId(recurring.id)}
                          title={t('accounting.editHistory.title')}
                        >
                          <History className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      <AddRecurringExpenseDialog
        restaurantId={restaurantId}
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onSuccess={() => refetchRecurring()}
      />

      {editingRecurring && (
        <EditRecurringExpenseDialog
          restaurantId={restaurantId}
          recurringExpense={editingRecurring}
          open={!!editingRecurring}
          onOpenChange={(open) => !open && setEditingRecurring(null)}
          onSuccess={() => refetchRecurring()}
        />
      )}

      <ConfirmModal
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        variant="destructive"
      />

      <AlertModal
        open={alertDialog.open}
        onOpenChange={(open) => setAlertDialog({ ...alertDialog, open })}
        title={alertDialog.title}
        description={alertDialog.description}
      />

      {/* Edit History Dialog */}
      <Dialog open={!!historyRecurringId} onOpenChange={(open) => !open && setHistoryRecurringId(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('accounting.editHistory.title')}</DialogTitle>
          </DialogHeader>
          <AccountingEditHistoryPanel
            entries={editHistory || []}
            isLoading={historyLoading}
            type="recurring_expense"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
